import { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Bookmark, MessageCircle, Flag, Star, BadgeCheck, ImagePlus, X, Camera, ShoppingBag, Users, Sparkles } from "lucide-react";
import { API, errMsg, inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PostMedia from "../components/PostMedia";
import Seo from "../components/Seo";
import ShareBar from "../components/ShareBar";
import GuessPrice from "../components/GuessPrice";
import ThisOrThat from "../components/ThisOrThat";

const FILTERS = [["all", "All"], ["look", "Looks"], ["photo", "Beauty"], ["styling", "Styling"], ["review", "Reviews"], ["question", "Questions"]];

function PostCard({ post }) {
  const { user } = useAuth();
  const [p, setP] = useState(post);
  const act = async (verb) => {
    if (!user) { toast.error("Please log in to interact"); return; }
    try { const { data } = await API.post(`/community/posts/${p.id}/${verb}`); setP({ ...p, [verb === "like" ? "liked" : "saved"]: data.liked ?? data.saved, like_count: p.like_count + ((verb === "like") ? (data.liked ? 1 : -1) : 0), save_count: p.save_count + ((verb === "save") ? (data.saved ? 1 : -1) : 0) }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const report = async () => { try { await API.post(`/community/posts/${p.id}/report`); toast.success("Reported to moderators"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <article className="break-inside-avoid mb-5 bg-white border border-[#EADBD8] rounded-2xl overflow-hidden shadow-soft hover:shadow-lift transition-shadow duration-300" data-testid={`community-post-${p.id}`}>
      {p.media?.[0] && (
        <div className="relative overflow-hidden">
          <PostMedia src={p.media[0]} className="w-full object-cover" />
          {p.products?.length > 0 && (
            <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/95 text-[#231518] text-[10px] font-bold px-2.5 py-1 rounded-full shadow-sm"><ShoppingBag className="w-3 h-3" /> Shop this look</span>
          )}
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-full bg-[#6A1B29] text-white grid place-items-center text-xs font-semibold shrink-0">{p.user_name?.[0] || "S"}</span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#231518] flex items-center gap-1 truncate">{p.user_name}{p.is_club && <BadgeCheck className="w-4 h-4 text-[#6A1B29]" />}</p>
            <p className="text-[11px] text-[#9E838B] capitalize">{p.type}{p.verified_purchase && " · Verified Purchase"}</p>
          </div>
          <button onClick={report} data-testid={`report-${p.id}`} className="ml-auto text-stone-300 hover:text-stone-500"><Flag className="w-4 h-4" /></button>
        </div>
        {p.rating > 0 && <div className="flex gap-0.5 mt-2.5">{[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-3.5 h-3.5 ${s <= p.rating ? "fill-[#6A1B29] text-[#6A1B29]" : "text-stone-200"}`} />)}</div>}
        <p className="text-sm text-[#231518] mt-2 leading-relaxed">{p.caption}</p>
        {p.products?.length > 0 ? (
          <div className="mt-3 space-y-2" data-testid={`post-shop-${p.id}`}>
            {p.products.map((pr) => (
              <Link key={pr.id} to={`/product/${pr.slug || pr.id}`} className="flex items-center gap-3 border border-[#EADBD8] rounded-xl p-2 hover:border-[#6A1B29] transition-colors" data-testid={`post-shop-item-${pr.id}`}>
                <img src={pr.image} alt="" className="w-11 h-11 rounded-lg object-cover" />
                <div className="flex-1 min-w-0"><p className="text-sm font-medium line-clamp-1 text-[#231518]">{pr.title}</p><p className="text-sm font-semibold text-[#6A1B29]">{inr(pr.selling_price)}</p></div>
                <span className="text-xs font-semibold text-[#6A1B29] shrink-0">Shop →</span>
              </Link>
            ))}
          </div>
        ) : p.product_title && <Link to={`/products`} className="inline-block mt-2 text-xs font-semibold text-[#6A1B29] bg-[#F9ECE8] px-3 py-1.5 rounded-full" data-testid={`post-product-${p.id}`}>🛍 {p.product_title}</Link>}
        <div className="flex items-center gap-5 mt-4 pt-3 border-t border-[#F3E7E4] text-[#9E838B]">
          <button onClick={() => act("like")} data-testid={`like-${p.id}`} className="flex items-center gap-1.5 text-sm hover:text-[#6A1B29] transition-colors"><Heart className={`w-5 h-5 ${p.liked ? "fill-[#6A1B29] text-[#6A1B29]" : ""}`} /> {p.like_count}</button>
          <span className="flex items-center gap-1.5 text-sm"><MessageCircle className="w-5 h-5" /> {p.comment_count}</span>
          <button onClick={() => act("save")} data-testid={`save-${p.id}`} className="ml-auto flex items-center gap-1.5 text-sm hover:text-[#6A1B29] transition-colors"><Bookmark className={`w-5 h-5 ${p.saved ? "fill-[#6A1B29] text-[#6A1B29]" : ""}`} /> {p.save_count}</button>
        </div>
      </div>
    </article>
  );
}

function ShareForm({ onDone, onClose }) {
  const { user } = useAuth();
  const fileRef = useRef();
  const [f, setF] = useState({ type: "photo", product_id: "", caption: "", rating: 5, recommend: true });
  const [media, setMedia] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { API.get("/products?limit=100").then((r) => setProducts(r.data.items)).catch(() => {}); }, []);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`; setMedia([url]); toast.success("Photo added"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const submit = async () => {
    if (!user) { toast.error("Please log in to share"); return; }
    if (!f.caption.trim()) { toast.error("Add a caption"); return; }
    setBusy(true);
    try { const { data } = await API.post("/community/posts", { ...f, media }); toast.success(data.message); onDone?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const FIELD = "w-full border border-[#EADBD8] bg-white rounded-xl px-4 py-3 text-sm outline-none focus:border-[#6A1B29]";
  return (
    <div className="bg-white border border-[#EADBD8] rounded-3xl p-6 sm:p-8 shadow-warm" data-testid="community-share-form">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Share Your Suvikka</p>
          <h3 className="font-display text-2xl font-semibold text-[#231518] mt-1">Found something you love? Style it. Share it. Inspire another woman.</h3>
        </div>
        <button onClick={onClose} className="text-stone-400 hover:text-stone-600" data-testid="share-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid gap-3 mt-5">
        <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={FIELD} data-testid="share-type">
          <option value="photo">Photo</option><option value="review">Review (verified purchase)</option><option value="styling">Styling</option><option value="question">Question</option>
        </select>
        <select value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })} className={FIELD} data-testid="share-product">
          <option value="">Tag a product (optional)</option>{products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        {f.type === "review" && (
          <div className="flex gap-1" data-testid="share-rating">{[1, 2, 3, 4, 5].map((s) => <button key={s} onClick={() => setF({ ...f, rating: s })}><Star className={`w-7 h-7 ${s <= f.rating ? "fill-[#6A1B29] text-[#6A1B29]" : "text-stone-200"}`} /></button>)}</div>
        )}
        <textarea placeholder="Share your experience…" rows={3} value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} className={FIELD} data-testid="share-caption" />
        <div className="flex items-center gap-3">
          <input ref={fileRef} type="file" accept="image/*,video/*" onChange={upload} className="hidden" />
          <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 text-sm font-semibold border border-[#EADBD8] px-4 py-2.5 rounded-xl hover:border-[#6A1B29]" data-testid="share-upload"><ImagePlus className="w-4 h-4" /> Add photo / video</button>
          {media[0] && <img src={media[0]} alt="preview" className="w-12 h-12 rounded-xl object-cover" />}
        </div>
        <button onClick={submit} disabled={busy} className="bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-6 py-3 rounded-full disabled:opacity-60" data-testid="share-submit">{busy ? "Sharing…" : "Share Your Look"}</button>
        <p className="text-xs text-[#9E838B]">Posts are reviewed by the community team before they appear. Verified reviews may qualify for a wallet reward after approval.</p>
      </div>
    </div>
  );
}

export default function Community() {
  const loc = useLocation();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(loc.pathname.endsWith("/share"));
  const load = () => API.get("/community/posts").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const shown = filter === "all" ? posts : posts.filter((p) => p.type === filter);
  return (
    <div className="motif-ivory min-h-[70vh]" data-testid="community-page">
      <Seo title="Suvikka Community | Real Finds, Real Experiences" description="The Suvikka community — real finds, real looks and real experiences from real women. See how Sakhis style and review Suvikka, and share your own." canonicalPath="/community" />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#2A1418] via-[#3d1a24] to-[#501325] text-white">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(#D4AF37 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center relative">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] inline-flex items-center gap-2 justify-center"><Users className="w-4 h-4" /> Suvikka Community</p>
          <h1 className="font-display text-4xl sm:text-6xl font-semibold mt-4 leading-tight">Real Women.<br /><span className="text-[#E88B88] italic">Real Experiences.</span></h1>
          <p className="text-white/85 mt-4 text-lg max-w-xl mx-auto">Looks, reviews and little finds shared by the Sakhis — shop what you love, straight from them.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <button onClick={() => setShowForm((s) => !s)} data-testid="toggle-share" className="bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-8 py-3.5 rounded-full transition-transform hover:scale-105">{showForm ? "Close" : "Show Us Your Suvikka"}</button>
            <Link to="/sakhi-wall" data-testid="community-wall-cta" className="inline-flex items-center gap-2 border border-white/30 hover:bg-white hover:text-[#501325] text-white font-semibold px-8 py-3.5 rounded-full transition-colors"><Sparkles className="w-4 h-4" /> Sakhi Wall</Link>
          </div>
        </div>
      </section>

      {/* FEED */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {showForm && <ShareForm onDone={() => { setShowForm(false); load(); }} onClose={() => setShowForm(false)} />}
        {posts.length > 0 && (
          <div className="flex gap-2 mt-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide" data-testid="community-filters">
            {FILTERS.map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`community-filter-${k}`}
                className={`shrink-0 text-sm font-medium px-4 py-2 rounded-full transition-colors ${filter === k ? "bg-[#6A1B29] text-white" : "bg-white border border-[#EADBD8] text-[#755C62] hover:border-[#6A1B29]"}`}>{l}</button>
            ))}
          </div>
        )}
        {shown.length === 0 ? (
          <div className="mt-6 bg-white border border-[#EADBD8] rounded-3xl p-12 text-center shadow-soft" data-testid="community-empty">
            <Camera className="w-9 h-9 text-[#6A1B29] mx-auto" />
            <p className="font-display text-2xl text-[#231518] mt-3">Be the first to share your Suvikka 💗</p>
            <button onClick={() => setShowForm(true)} className="inline-block mt-5 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-6 py-3 rounded-full text-sm" data-testid="community-empty-cta">Show us your look</button>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 mt-6" data-testid="community-grid">
            {shown.map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        )}
      </section>

      {/* PLAYFUL ENGAGEMENT */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-10 space-y-6">
        <GuessPrice />
        <ThisOrThat />
      </section>

      {/* SHOW US YOUR SUVIKKA band */}
      <section className="bg-[#2A1418] text-white" data-testid="show-your-suvikka">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37]">Your Turn</p>
          <h2 className="font-display text-3xl sm:text-5xl font-semibold mt-3">Show Us Your Suvikka.</h2>
          <p className="text-stone-300 mt-3 max-w-xl mx-auto">Found something you love? Style it. Share it. Inspire another woman — and shop it in a tap.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-7">
            <button onClick={() => { setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="band-share-cta" className="bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-8 py-3.5 rounded-full transition-transform hover:scale-105"><Camera className="w-4 h-4 inline mr-1.5" /> Share Your Look</button>
            <ShareBar compact text="Join the Suvikka community 💗" />
          </div>
        </div>
      </section>
    </div>
  );
}
